/**
 * Page admin : Banque des sujets d'examens.
 * Upload PDF + listing + suppression. Accès : scolarité / admin / directeur.
 */
import { useState, useMemo } from 'react';
import {
  Library, Upload, Trash2, Search, X, Loader2, FileText, Plus, Calendar,
} from 'lucide-react';
import {
  useSujetsExamensAdminQuery, useUploaderSujetExamenMutation, useSupprimerSujetExamenMutation,
  type SujetExamenItem,
} from '../../services/formateurApi';

const NIVEAUX = ['L1', 'L2', 'L3', 'M1', 'M2'];

export default function BanqueSujetsAdmin() {
  const [search, setSearch] = useState('');
  const [niveau, setNiveau] = useState('');
  const [annee, setAnnee] = useState('');
  const [showUpload, setShowUpload] = useState(false);

  const { data, isLoading, refetch } = useSujetsExamensAdminQuery({
    matiere: search || undefined,
    niveau: niveau || undefined,
    annee_academique: annee || undefined,
  });
  const [supprimer] = useSupprimerSujetExamenMutation();

  const sujets = data?.results || [];
  const anneesDispo = useMemo(
    () => Array.from(new Set(sujets.map(s => s.annee_academique))).sort().reverse(),
    [sujets],
  );

  const handleDelete = async (s: SujetExamenItem) => {
    if (!window.confirm(`Supprimer le sujet « ${s.titre} » ?`)) return;
    try { await supprimer(s.id).unwrap(); refetch(); } catch {}
  };

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
      <header className="mb-4 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Library size={22} className="text-blue-600" /> Banque des sujets d'examens
          </h1>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
            Uploadez les sujets PDF — ils seront accessibles aux étudiants pour révision.
          </p>
        </div>
        <button onClick={() => setShowUpload(true)}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg inline-flex items-center gap-1.5 text-sm font-semibold">
          <Upload size={14} /> Nouveau sujet
        </button>
      </header>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 mb-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div className="relative">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Matière, titre…"
            className="w-full pl-7 pr-2 py-1.5 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100" />
        </div>
        <select value={niveau} onChange={(e) => setNiveau(e.target.value)}
          className="w-full px-2 py-1.5 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100">
          <option value="">Tous niveaux</option>
          {NIVEAUX.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <select value={annee} onChange={(e) => setAnnee(e.target.value)}
          className="w-full px-2 py-1.5 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100">
          <option value="">Toutes années</option>
          {anneesDispo.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      {isLoading && (
        <div className="text-center py-8 text-gray-500 text-sm">
          <Loader2 size={14} className="inline animate-spin mr-2" /> Chargement…
        </div>
      )}

      {!isLoading && sujets.length === 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-8 text-center text-gray-500">
          <Library size={36} className="inline mb-2 opacity-50" />
          <div className="text-sm">Aucun sujet dans la banque pour ces filtres.</div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {sujets.map(s => (
          <article key={s.id}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3">
            <div className="flex items-start gap-2 mb-2">
              <div className="w-9 h-9 rounded bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center flex-shrink-0">
                <FileText size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white truncate">{s.titre}</h3>
                <div className="text-[10px] text-gray-500 truncate">{s.matiere_libelle}</div>
              </div>
              <button onClick={() => handleDelete(s)}
                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded">
                <Trash2 size={13} />
              </button>
            </div>
            <div className="flex flex-wrap gap-1 text-[10px]">
              <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                {s.niveau}
              </span>
              <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                <Calendar size={9} className="inline mr-0.5" /> {s.annee_academique}
              </span>
              {s.option && (
                <span className="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded">
                  {s.option}
                </span>
              )}
              {s.session === 'rattrapage' && (
                <span className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded">
                  Rattrapage
                </span>
              )}
            </div>
            <div className="text-[10px] text-gray-400 mt-2 flex items-center justify-between">
              <span>{s.taille_humaine} • {s.nb_telechargements} téléch.</span>
              {s.fichier_url && (
                <a href={s.fichier_url} target="_blank" rel="noreferrer"
                  className="text-blue-500 hover:text-blue-600">Voir</a>
              )}
            </div>
          </article>
        ))}
      </div>

      {showUpload && (
        <ModalUpload onClose={() => setShowUpload(false)}
          onDone={() => { setShowUpload(false); refetch(); }} />
      )}
    </div>
  );
}

function ModalUpload({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [titre, setTitre] = useState('');
  const [matiere, setMatiere] = useState('');
  const [matiereCode, setMatiereCode] = useState('');
  const [niveau, setNiveau] = useState('L1');
  const [option, setOption] = useState('');
  const [annee, setAnnee] = useState('');
  const [semestre, setSemestre] = useState('');
  const [session, setSession] = useState<'normale' | 'rattrapage'>('normale');
  const [description, setDescription] = useState('');
  const [fichier, setFichier] = useState<File | null>(null);
  const [erreur, setErreur] = useState('');
  const [uploader, { isLoading }] = useUploaderSujetExamenMutation();

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    setErreur('');
    if (!titre || !matiere || !niveau || !annee || !fichier) {
      setErreur('Titre, matière, niveau, année et fichier requis');
      return;
    }
    const fd = new FormData();
    fd.append('titre', titre);
    fd.append('matiere_libelle', matiere);
    fd.append('matiere_code', matiereCode);
    fd.append('niveau', niveau);
    fd.append('option', option);
    fd.append('annee_academique', annee);
    fd.append('semestre', semestre);
    fd.append('session', session);
    fd.append('description', description);
    fd.append('fichier', fichier);
    try {
      await uploader(fd).unwrap();
      onDone();
    } catch (err: any) {
      setErreur(err?.data?.detail || 'Erreur upload');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}>
      <form onSubmit={handle} onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg border border-gray-200 dark:border-gray-700 max-h-[90vh] overflow-y-auto">
        <header className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white inline-flex items-center gap-2">
            <Upload size={18} className="text-blue-600" /> Nouveau sujet d'examen
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </header>

        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Titre *</label>
            <input value={titre} onChange={(e) => setTitre(e.target.value)} required
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Matière (libellé) *</label>
              <input value={matiere} onChange={(e) => setMatiere(e.target.value)} required
                className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Code matière</label>
              <input value={matiereCode} onChange={(e) => setMatiereCode(e.target.value)}
                className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Niveau *</label>
              <select value={niveau} onChange={(e) => setNiveau(e.target.value)}
                className="w-full px-2 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100">
                {NIVEAUX.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Année *</label>
              <input value={annee} onChange={(e) => setAnnee(e.target.value)}
                placeholder="2022-2023" required
                className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Semestre</label>
              <select value={semestre} onChange={(e) => setSemestre(e.target.value)}
                className="w-full px-2 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100">
                <option value="">—</option>
                <option value="S1">S1</option>
                <option value="S2">S2</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Option</label>
              <input value={option} onChange={(e) => setOption(e.target.value)}
                placeholder="Ex: Maintenance Industrielle"
                className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Session</label>
              <select value={session} onChange={(e) => setSession(e.target.value as any)}
                className="w-full px-2 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100">
                <option value="normale">Normale</option>
                <option value="rattrapage">Rattrapage</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Description (optionnel)</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)}
              rows={2} maxLength={2000}
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 resize-none" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Fichier PDF * (max 20 Mo)</label>
            <input type="file" accept=".pdf,application/pdf" required
              onChange={(e) => setFichier(e.target.files?.[0] || null)}
              className="w-full text-sm text-gray-700 dark:text-gray-300 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-blue-50 dark:file:bg-blue-900/30 file:text-blue-700 dark:file:text-blue-300" />
          </div>
          {erreur && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded p-2 text-sm">
              {erreur}
            </div>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded">
            Annuler
          </button>
          <button type="submit" disabled={isLoading}
            className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold rounded inline-flex items-center gap-1.5">
            {isLoading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            {isLoading ? 'Upload…' : 'Uploader'}
          </button>
        </footer>
      </form>
    </div>
  );
}
